import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { PlatformUserRow } from '@etiquetachef/types';

type Props = {
  /** Pessoa multi-empresa; `null` fecha o diálogo. */
  user: PlatformUserRow | null;
  onClose: () => void;
  onSelect: (tenantId: string) => void;
};

/**
 * Pergunta em QUAL empresa aplicar uma ação que vale por membership (papel,
 * credencial, remoção).
 *
 * Existe porque a tela lista pessoas, não memberships: sem este passo, alguém
 * com duas empresas teria a ação aplicada na primeira da lista — errado, e em
 * silêncio. Quando há uma empresa só, o container pula este diálogo.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 17:08 +0200.
 *
 * Usado por: `routes/UsersPage.tsx` (antes de `MemberRoleDialog`,
 * `MemberCredentialDialog` e da remoção).
 */
export function TenantPickerDialog({ user, onClose, onSelect }: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    setTenantId(user?.memberships[0]?.tenantId ?? '');
  }, [user]);

  if (!user) return null;

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={t('adminPlatformUsers.tenantPicker.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              variant="primary"
              disabled={tenantId.length === 0}
              onClick={(): void => {
                onSelect(tenantId);
              }}
            >
              {t('adminPlatformUsers.tenantPicker.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label={t('adminPlatformUsers.tenantPicker.label')}
        description={t('adminPlatformUsers.tenantPicker.hint')}
      >
        <RadioGroup
          value={tenantId}
          onChange={({ detail }): void => {
            setTenantId(detail.value);
          }}
          items={user.memberships.map((m) => ({
            value: m.tenantId,
            label: m.tenantName,
            description: t(`adminMembers.roles.${m.role}`),
          }))}
        />
      </FormField>
    </Modal>
  );
}
